/**
 * src/version-sync.js - Non-Destructive Corner Version Badge Synchronizer
 * Standardized Recipe from Antigravity Workspace Standards
 *
 * Protects pre-rendered HTML badge paint and eradicates visual downgrade flicker.
 * Prohibits stale runtime JavaScript from overwriting newer DOM watermarks.
 */

export function syncCornerVersionBadge(versionString) {
  if (typeof document === 'undefined') return;
  const badge = document.getElementById('corner-version-badge') || document.querySelector('.corner-version-badge');
  if (!badge) return;

  const currentDomText = badge.textContent ? badge.textContent.trim().replace(/^v/, '') : '';
  const newVer = versionString ? String(versionString).trim().replace(/^v/, '') : '';

  // If DOM badge is already rendered, only update if the new version is strictly newer
  if (currentDomText && newVer) {
    if (currentDomText === newVer) return; // In lockstep: zero-op
    // Prevent accidental downgrade overwrite (e.g. stale 0.0.1 overwriting 0.0.12)
    if (!isNewerVersion(newVer, currentDomText)) {
      console.warn(`[VersionSync] Blocked attempt to downgrade DOM badge from v${currentDomText} to v${newVer}`);
      return;
    }
  }

  if (newVer) {
    badge.textContent = `v${newVer}`;
  }
}

export function resolveEffectiveAppVersion(buildVersion, fallbackVersion = '0.0.19') {
  if (typeof document === 'undefined') return buildVersion || fallbackVersion;

  const meta = document.querySelector('meta[name="app-version"]');
  const metaVer = meta && meta.content ? meta.content.trim().replace(/^v/, '') : null;

  const badge = document.getElementById('corner-version-badge') || document.querySelector('.corner-version-badge');
  const badgeVer = badge && badge.textContent ? badge.textContent.trim().replace(/^v/, '') : null;

  const rawBuild = buildVersion ? String(buildVersion).trim().replace(/^v/, '') : null;

  // Find highest valid semver among discovered candidates
  const candidates = [metaVer, badgeVer, rawBuild, fallbackVersion].filter(Boolean);
  let highest = candidates[0];
  for (let i = 1; i < candidates.length; i++) {
    if (isNewerVersion(candidates[i], highest)) {
      highest = candidates[i];
    }
  }
  return highest;
}

export function isNewerVersion(target, base) {
  if (!target || !base) return false;
  if (target === base) return false;
  const t = target.split('.').map(Number);
  const b = base.split('.').map(Number);
  for (let i = 0; i < Math.max(t.length, b.length); i++) {
    const targetPart = t[i] || 0;
    const basePart = b[i] || 0;
    if (targetPart > basePart) return true;
    if (targetPart < basePart) return false;
  }
  return false;
}
